import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from '../../axios';
import type { OAuthProvider } from './oauth';
import {
  clearStoredAuth,
  readStoredAccessToken,
  readStoredRefreshToken,
  storeAuthTokens,
} from './authStorage';

type AuthStatus = 'idle' | 'loading' | 'succeeded' | 'failed';
type MfaMethod = 'totp';

interface PendingMfaChallenge {
  mfaToken: string;
  method: MfaMethod;
  mfaTokenExpiresIn: string | null;
}

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  pendingMfa: PendingMfaChallenge | null;
  status: AuthStatus;
  error: string | null;
}

type AuthTokensPayload = {
  token?: string;
  accessToken?: string;
  refreshToken?: string;
};

type MfaChallengePayload = {
  mfaRequired?: boolean;
  mfaToken?: string;
  mfaTokenExpiresIn?: string | number;
  method?: MfaMethod;
};

type AuthApiResponse = AuthTokensPayload & MfaChallengePayload;

type StoredAuthTokens = {
  token: string;
  refreshToken: string;
};

type AuthFlowResult =
  | {
      kind: 'authenticated';
      tokens: StoredAuthTokens;
    }
  | {
      kind: 'mfa_required';
      challenge: PendingMfaChallenge;
    };

const resolveAuthTokens = (
  payload: AuthTokensPayload | null | undefined
): StoredAuthTokens | null => {
  const token = String(payload?.accessToken || payload?.token || '').trim();
  const refreshToken = String(payload?.refreshToken || '').trim();
  if (!token || !refreshToken) {
    return null;
  }

  return { token, refreshToken };
};

const resolveMfaChallenge = (
  payload: MfaChallengePayload | null | undefined
): PendingMfaChallenge | null => {
  if (payload?.mfaRequired !== true) {
    return null;
  }

  const mfaToken = String(payload?.mfaToken || '').trim();
  if (!mfaToken) {
    return null;
  }

  const method = payload?.method === 'totp' ? 'totp' : 'totp';
  const rawExpiry =
    payload?.mfaTokenExpiresIn === undefined ||
    payload?.mfaTokenExpiresIn === null
      ? ''
      : String(payload.mfaTokenExpiresIn).trim();

  return {
    mfaToken,
    method,
    mfaTokenExpiresIn: rawExpiry || null,
  };
};

const resolveAuthFlow = (
  payload: AuthApiResponse | null | undefined
): AuthFlowResult | null => {
  const tokens = resolveAuthTokens(payload);
  if (tokens) {
    return { kind: 'authenticated', tokens };
  }

  const challenge = resolveMfaChallenge(payload);
  if (challenge) {
    return { kind: 'mfa_required', challenge };
  }

  return null;
};

const initialAccessToken = readStoredAccessToken();
const initialRefreshToken = readStoredRefreshToken();

if (initialAccessToken && !initialRefreshToken) {
  clearStoredAuth();
}

const initialState: AuthState = {
  token: initialAccessToken && initialRefreshToken ? initialAccessToken : null,
  refreshToken: initialRefreshToken,
  pendingMfa: null,
  status: 'idle',
  error: null,
};

export const login = createAsyncThunk<
  AuthFlowResult,
  { email: string; password: string },
  { rejectValue: string }
>('auth/login', async ({ email, password }, { rejectWithValue }) => {
  try {
    const res = await axios.post<AuthApiResponse>('/auth/login', { email, password });
    const flow = resolveAuthFlow(res.data);
    if (!flow) {
      return rejectWithValue('Server did not return a valid authentication response');
    }

    if (flow.kind === 'authenticated') {
      storeAuthTokens({
        accessToken: flow.tokens.token,
        refreshToken: flow.tokens.refreshToken,
      });
    }

    return flow;
  } catch (err: any) {
    const msg =
      err?.response?.data?.message ||
      err?.message ||
      'Login failed';
    return rejectWithValue(msg);
  }
});

export const loginWithOAuth = createAsyncThunk<
  AuthFlowResult,
  { provider: OAuthProvider; code: string; state: string },
  { rejectValue: string }
>('auth/loginWithOAuth', async ({ provider, code, state }, { rejectWithValue }) => {
  try {
    const res = await axios.post<AuthApiResponse>('/auth/oauth/exchange', {
      provider,
      code,
      state,
    });
    const flow = resolveAuthFlow(res.data);
    if (!flow) {
      return rejectWithValue('Server did not return a valid authentication response');
    }

    if (flow.kind === 'authenticated') {
      storeAuthTokens({
        accessToken: flow.tokens.token,
        refreshToken: flow.tokens.refreshToken,
      });
    }

    return flow;
  } catch (err: any) {
    const msg = err?.response?.data?.message || err?.message || 'OAuth login failed';
    return rejectWithValue(msg);
  }
});

export const verifyAdminMfa = createAsyncThunk<
  StoredAuthTokens,
  { mfaToken: string; code: string },
  { rejectValue: string }
>('auth/verifyAdminMfa', async ({ mfaToken, code }, { rejectWithValue }) => {
  try {
    const res = await axios.post<AuthTokensPayload>('/auth/mfa/verify', {
      mfaToken,
      code,
    });
    const tokens = resolveAuthTokens(res.data);
    if (!tokens) {
      return rejectWithValue('Server did not return auth tokens');
    }

    storeAuthTokens({
      accessToken: tokens.token,
      refreshToken: tokens.refreshToken,
    });

    return tokens;
  } catch (err: any) {
    const msg =
      err?.response?.data?.message ||
      err?.message ||
      'MFA verification failed';
    return rejectWithValue(msg);
  }
});

export const logoutUser = createAsyncThunk<void, void>(
  'auth/logoutUser',
  async () => {
    const refreshToken = readStoredRefreshToken();
    try {
      if (refreshToken) {
        await axios.post(
          '/auth/logout',
          { refreshToken },
          {
            headers: {
              'x-skip-auth-refresh': '1',
            },
          }
        );
      }
    } catch {
      // Ignore transport errors on logout.
    } finally {
      clearStoredAuth();
    }
  }
);

export const validateToken = createAsyncThunk<
  true,
  void,
  { rejectValue: 'invalid_token' | string }
>('auth/validateToken', async (_: void, { rejectWithValue }) => {
  try {
    await axios.get('/bots');
    return true;
  } catch (err: any) {
    if (err?.response?.status === 401 || err?.response?.status === 403) {
      clearStoredAuth();
      return rejectWithValue('invalid_token');
    }

    const msg =
      err?.response?.data?.message ||
      err?.message ||
      'Could not validate the current session';
    return rejectWithValue(msg);
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthSession(state, action: PayloadAction<StoredAuthTokens | null>) {
      if (action.payload) {
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.pendingMfa = null;
        state.status = 'succeeded';
        state.error = null;
        storeAuthTokens({
          accessToken: action.payload.token,
          refreshToken: action.payload.refreshToken,
        });
      } else {
        state.token = null;
        state.refreshToken = null;
        state.pendingMfa = null;
        state.status = 'idle';
        state.error = null;
        clearStoredAuth();
      }
    },
    setToken(state, action: PayloadAction<string | null>) {
      state.token = action.payload;
      if (action.payload) {
        state.pendingMfa = null;
        storeAuthTokens({ accessToken: action.payload });
        state.status = 'succeeded';
        state.error = null;
      } else {
        state.refreshToken = null;
        state.pendingMfa = null;
        state.status = 'idle';
        clearStoredAuth();
      }
    },
    clearPendingMfa(state) {
      state.pendingMfa = null;
      state.status = state.token ? 'succeeded' : 'idle';
      state.error = null;
    },
    logout(state) {
      state.token = null;
      state.refreshToken = null;
      state.pendingMfa = null;
      state.status = 'idle';
      state.error = null;
      clearStoredAuth();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.pendingMfa = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.error = null;

        if (action.payload.kind === 'authenticated') {
          state.status = 'succeeded';
          state.token = action.payload.tokens.token;
          state.refreshToken = action.payload.tokens.refreshToken;
          state.pendingMfa = null;
          return;
        }

        state.status = 'idle';
        state.token = null;
        state.refreshToken = null;
        state.pendingMfa = action.payload.challenge;
        clearStoredAuth();
      })
      .addCase(login.rejected, (state, action) => {
        state.status = 'failed';
        state.token = null;
        state.refreshToken = null;
        state.pendingMfa = null;
        state.error = (action.payload as string) ?? 'Login failed';
        clearStoredAuth();
      })
      .addCase(loginWithOAuth.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.pendingMfa = null;
      })
      .addCase(loginWithOAuth.fulfilled, (state, action) => {
        state.error = null;

        if (action.payload.kind === 'authenticated') {
          state.status = 'succeeded';
          state.token = action.payload.tokens.token;
          state.refreshToken = action.payload.tokens.refreshToken;
          state.pendingMfa = null;
          return;
        }

        state.status = 'idle';
        state.token = null;
        state.refreshToken = null;
        state.pendingMfa = action.payload.challenge;
        clearStoredAuth();
      })
      .addCase(loginWithOAuth.rejected, (state, action) => {
        state.status = 'failed';
        state.token = null;
        state.refreshToken = null;
        state.pendingMfa = null;
        state.error = (action.payload as string) ?? 'OAuth login failed';
        clearStoredAuth();
      })
      .addCase(verifyAdminMfa.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(verifyAdminMfa.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.pendingMfa = null;
        state.error = null;
      })
      .addCase(verifyAdminMfa.rejected, (state, action) => {
        const errorMessage =
          (action.payload as string) ?? 'MFA verification failed';

        state.status = 'failed';
        state.token = null;
        state.refreshToken = null;
        state.error = errorMessage;

        if (errorMessage === 'Invalid or expired MFA token') {
          state.pendingMfa = null;
          clearStoredAuth();
        }
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.token = null;
        state.refreshToken = null;
        state.pendingMfa = null;
        state.status = 'idle';
        state.error = null;
      })
      .addCase(validateToken.fulfilled, () => {
        // Token is valid or was refreshed transparently.
      })
      .addCase(validateToken.rejected, (state, action) => {
        state.token = null;
        state.refreshToken = null;
        state.pendingMfa = null;
        state.error =
          action.payload === 'invalid_token' ? null : (action.payload as string);
        clearStoredAuth();
      });
  },
});

export const { logout, setAuthSession, setToken, clearPendingMfa } =
  authSlice.actions;
export default authSlice.reducer;

export const selectAuthToken = (state: any) => state.auth.token as string | null;
export const selectIsAuthed = (state: any) => Boolean(state.auth.token);
export const selectAuthStatus = (state: any) => state.auth.status as AuthStatus;
export const selectAuthError = (state: any) => state.auth.error as string | null;
export const selectPendingMfa = (state: any) =>
  state.auth.pendingMfa as PendingMfaChallenge | null;
